import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Alert, Box, CircularProgress, Typography, } from "@mui/material";
import taskerService, {
  type TaskerTask, } from "../../../services/taskerService";
import {
  SchemaRenderer, type SchemaRendererHandle, } from "../components/SchemaRenderer";
import { readErrorMessage } from "../utils/mapBeErrors";
import { ReviewerActionsFooter } from "./ReviewerActionsFooter";
import { useRemarksMaxLength } from "./useRemarksMaxLength";
import { CommercialTermsPreview } from "./CommercialTermsPreview";
import { useLatestFormData, useReviewerTaskType } from "./reviewerQueries";
import { transitionForms } from "./taskConfig";
import {
  ReviewerAssignmentCard, type ReviewerSelection, } from "./ReviewerAssignmentCard";
import { PriorReviewerRemarks } from "./PriorReviewerRemarks";
import { useUsersByRole } from "../hooks/useUsersByRole";
import { type BouncerRoleUser } from "../../../services/bouncerUsers";
import { type OnTransitionSubmitted } from "./ReviewerRouter";

import {
  REVIEWER_APPROVED_MSG, REVIEWER_REJECTED_MSG, REVIEWER_SEND_BACK_MSG_TO_VENDOR, REVIEWER_REMARKS_FIELD, reviewerFormId } from "./reviewerRoles";

/**
 * Rebuilds the reviewer picker's value from what the form saved. The field is
 * multi-value since ENG-27858, so it comes back as an array — but a draft saved
 * before that, or against a form not yet switched over, still holds a bare
 * string. An address that no longer holds the role is kept as a synthetic entry
 * so the picker shows it rather than silently dropping the VM's pick.
 */
function resolveSavedReviewers(
  saved: unknown,
  users: BouncerRoleUser[] | undefined,
): BouncerRoleUser[] {
  const emails =
    typeof saved === "string"
      ? [saved]
      : Array.isArray(saved)
        ? saved.filter((e): e is string => typeof e === "string")
        : [];
  return emails
    .map((e) => e.trim())
    .filter(Boolean)
    .map(
      (email) =>
        users?.find((u) => u.email_id === email) ?? {
          user_id: -1,
          user_name: email,
          email_id: email,
          role_id: -1,
          role_name: "",
          user_to_role_id: -1,
        },
    );
}

type Props = { task: TaskerTask; onSubmitted: OnTransitionSubmitted };

export function VmReviewPanel({ task, onSubmitted }: Props) {
  const rendererRef = useRef<SchemaRendererHandle | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reviewers, setReviewers] = useState<ReviewerSelection>({ cm: [] });

  const taskTypeQuery = useReviewerTaskType(task.task_type_id);
  const remarksMaxLength = useRemarksMaxLength(
    reviewerFormId(taskTypeQuery.data?.task_config, "vm"),
    REVIEWER_REMARKS_FIELD.vm,
  );

  const enrichmentFormId = useMemo(() => {
    return transitionForms(taskTypeQuery.data?.task_config)?.vm_enrichment ?? null;
  }, [taskTypeQuery.data]);

  const formSchemaQuery = useQuery({
    queryKey: ["reviewer-form-schema", enrichmentFormId, task.id],
    queryFn: () =>
      taskerService.getFormSchema(enrichmentFormId as string, task.id, "reviewer"),
    enabled: Boolean(enrichmentFormId && task.id),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const savedEnrichmentQuery = useLatestFormData(
    "reviewer-vm-enrichment-saved",
    task.id,
    enrichmentFormId ?? undefined,
  );

  const cmUsersQuery = useUsersByRole("CATEGORY_MANAGER");

  const [reviewersHydrated, setReviewersHydrated] = useState(false);
  useEffect(() => {
    if (reviewersHydrated) return;
    if (savedEnrichmentQuery.isInitialLoading) return;
    if (cmUsersQuery.isInitialLoading) return;

    const saved = savedEnrichmentQuery.data ?? {};
    const cm = resolveSavedReviewers(
      saved.category_manager_email,
      cmUsersQuery.data,
    );
    if (cm.length) setReviewers({ cm });
    setReviewersHydrated(true);
  }, [
    reviewersHydrated,
    savedEnrichmentQuery.data,
    savedEnrichmentQuery.isInitialLoading,
    cmUsersQuery.data,
    cmUsersQuery.isInitialLoading,
  ]);

  const filteredFields = useMemo(() => {
    const HIDDEN = new Set([
      "category_manager_email",
      "biz_fin_email",
      "auto_invoice_validate",
      "three_vendor_quotes_available",
    ]);
    return (formSchemaQuery.data?.fields ?? []).filter(
      (f) => !HIDDEN.has(f.name),
    );
  }, [formSchemaQuery.data?.fields]);

  const handleApprove = () => {
    setError(null);
    if (!reviewers.cm.length) {
      setError("Select at least one Category Manager before approving.");
      return;
    }
    rendererRef.current?.submit();
  };

  const onSchemaSubmit = async (form_data: Record<string, unknown>) => {
    if (!task.id) return;
    if (!reviewers.cm.length) {
      setError("Select at least one Category Manager before approving.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await taskerService.executeTransition(
        task.id,
        "vm_approved",
        {
          ...form_data,
          category_manager_email: reviewers.cm.map((u) => u.email_id),
        },
        "reviewer",
      );
      onSubmitted("approved", REVIEWER_APPROVED_MSG.vm);
    } catch (err) {
      setError(
        readErrorMessage(err, "Couldn't submit approval. Try again."),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async (reason: string) => {
    if (!task.id) return;
    setSubmitting(true);
    setError(null);
    try {
      await taskerService.executeTransition(
        task.id,
        "vm_reject",
        { vm_remarks: reason },
        "reviewer",
      );
      onSubmitted("rejected", REVIEWER_REJECTED_MSG);
    } catch (err) {
      setError(readErrorMessage(err, "Couldn't submit rejection. Try again."));
      // Re-throw so ReviewerActionsFooter.confirm() sees the reject and keeps
      // the dialog open with the typed reason preserved. The setError above
      // still lands for the panel-level errorMessage banner.
      throw err;
    } finally {
      setSubmitting(false);
    }
  };

  const handleSave = async () => {
    if (!task.id || !enrichmentFormId) return;
    const values = rendererRef.current?.getValues();
    if (!values) return;
    setError(null);
    setSaving(true);
    try {
      const payload: Record<string, unknown> = { ...values };
      if (reviewers.cm.length) {
        payload.category_manager_email = reviewers.cm.map((u) => u.email_id);
      }
      await taskerService.upsertStepFormData(
        enrichmentFormId,
        task.id,
        payload,
        "reviewer",
      );
      setLastSavedAt(new Date());
      rendererRef.current?.markSaved();
    } catch (err) {
      setError(readErrorMessage(err, "Couldn't save. Try again."));
    } finally {
      setSaving(false);
    }
  };

  const handleSendBack = async (reason: string) => {
    if (!task.id) return;
    setSubmitting(true);
    setError(null);
    try {
      await taskerService.executeTransition(
        task.id,
        "vm_send_back",
        { vm_remarks: reason },
        "reviewer",
      );
      onSubmitted("rejected", REVIEWER_SEND_BACK_MSG_TO_VENDOR);
    } catch (err) {
      setError(readErrorMessage(err, "Couldn't send back. Try again."));
      // See handleReject — re-throw so the footer preserves the typed reason.
      throw err;
    } finally {
      setSubmitting(false);
    }
  };

  if (taskTypeQuery.isInitialLoading || formSchemaQuery.isInitialLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (taskTypeQuery.isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load task-type config:{" "}
          {taskTypeQuery.error instanceof Error
            ? taskTypeQuery.error.message
            : "Unknown error"}
        </Alert>
      </Box>
    );
  }

  if (!taskTypeQuery.data) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Task-type config not yet loaded. If this persists, the task_type_id{" "}
          <code>{task.task_type_id ?? "(missing)"}</code> may not exist.
        </Alert>
      </Box>
    );
  }

  if (!enrichmentFormId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          VM enrichment form is not configured on this task type
          (task_config.transition_forms.vm_enrichment is missing).
        </Alert>
      </Box>
    );
  }

  if (formSchemaQuery.isError || !formSchemaQuery.data) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load VM enrichment form:{" "}
          {formSchemaQuery.error instanceof Error
            ? formSchemaQuery.error.message
            : "Unknown error"}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 0.5 }}>
        VM enrichment & commercial terms
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2.5 }}>
        Commercial fields are internal only — never visible to the vendor.
      </Typography>

      <PriorReviewerRemarks
        task={task}
        taskConfig={taskTypeQuery.data?.task_config}
        authMode="reviewer"
        excludeRole="vm"
      />

      <SchemaRenderer
        ref={rendererRef}
        fields={filteredFields}
        scope={formSchemaQuery.data.scope}
        onSubmit={onSchemaSubmit}
        columns={1}
        initialValues={savedEnrichmentQuery.data}
        attachmentOwner={{
          ownerRef: task.id,
          ownerType: "TASK",
          authMode: "reviewer",
        }}
        sections={formSchemaQuery.data.metadata?.sections}
        sectionExtras={{ commercial_terms: <CommercialTermsPreview /> }}
      />

      <ReviewerAssignmentCard
        value={reviewers}
        onChange={setReviewers}
        disabled={submitting}
      />

      <ReviewerActionsFooter
        reasonMaxLength={remarksMaxLength}
        submitting={submitting}
        errorMessage={error}
        onApprove={handleApprove}
        onReject={handleReject}
        onSendBack={handleSendBack}
        onSave={handleSave}
        saving={saving}
        lastSavedAt={lastSavedAt}
      />
    </Box>
  );
}
